/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from '@chakra-ui/react'
import { Popper, ClickAwayListener, Paper } from "@mui/material";
import { Tooltip, IconButton } from "@mui/material";
import { Tooltip as MuiTooltip } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  MdBlock,
  MdOutlineDone,
  MdClose,
  MdOutlineContentCopy,
  MdOutlineArrowOutward,
  MdMoreHoriz,
  MdCurrencyExchange,
  MdOutlinePermIdentity,
} from "react-icons/md";

import { ProfileContext } from "../../../../store/ProfileProvider";
import TradeCancelConfirmModal from "../../shared/TradeCancelConfirmModal";
import TradeCompleteConfirmModal from "../../shared/TradeCompleteConfirmModal";
import TradeRejectConfirmModal from "../../shared/TradeRejectConfirmModal";
import SweetAlert2 from "react-sweetalert2";
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = { id: string; name: string };
type Subaccount = { id: string; name: string };
type Address = { id: string; address: string; nickname: string };
type Offer = {
  receive_asset: string;
  receive: number;
  original_rate: number;
  margin: number;
  sell_asset: string;
  sell: string;
  expired_text: string;
  offer_id: string;
  expired: string;
  settlement_address: string;
  rate: number;
  original_receive: number;
  status: string;
};

type OfferHistory = Offer[];

type RowData = {
  id: string;
  status: "awaiting" | "pending" | "cancelled" | "completed" | "rejected";
  shortid: string;
  offer_history: OfferHistory;
  accepted_offer: Offer;

  from_amount: number | string;
  from_asset: string;
  from_network: string;
  from_asset_decimal: number;

  fee_amount?: number | string | null;
  fee_asset?: string | null;
  net_amount?: number | string | null;
  net_asset?: string | null;

  to_asset: string;
  to_network: string;
  to_address: Address;
  to_asset_decimal: number;

  created_at: string;
  merchant: Merchant;
  subaccount: Subaccount;

  transactions: any[];
  reason?: string;
  transaction_reference?: string;

  rate?: number | string | null;
  rate_pair?: string | null;
};

type ActionMenuCellProps = {
  row: any;
  userPermissions: any;
  onCancel: (data: any) => void;
  onComplete: (data: any) => void;
  onReject: (data: any) => void;
};

const ActionMenuCell: React.FC<ActionMenuCellProps> = ({
  row,
  userPermissions,
  onCancel,
  onComplete,
  onReject,
}) => {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const status = row?.status;
  const id = row?.id;

  const canCancel =
    userPermissions?.cancelTrade === true && status === "awaiting";
  const canComplete =
    userPermissions?.completeTrade === true && status === "pending";
  const canReject =
    userPermissions?.rejectTrade === true && status === "pending";

  const items: {
    key: "complete" | "reject" | "cancel";
    label: string;
    icon: React.ReactNode;
    action: () => void;
  }[] = [];
  if (canComplete)
    items.push({
      key: "complete",
      label: "Mark as Completed",
      icon: <MdOutlineDone className="h-4 w-4" />,
      action: () => onComplete(row),
    });
  if (canReject)
    items.push({
      key: "reject",
      label: "Mark as Reject",
      icon: <MdClose className="h-4 w-4" />,
      action: () => onReject(row),
    });
  if (canCancel)
    items.push({
      key: "cancel",
      label: "Mark as Cancel",
      icon: <MdBlock className="h-4 w-4" />,
      action: () => onCancel(row),
    });

  if (items.length === 0) {
    return (
      <span
        title="No action available"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-400"
      >
        <MdMoreHoriz className="h-5 w-5" />
      </span>
    );
  }

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setOpen((s) => !s);
  };
  const handleClose = () => setOpen(false);

  const hoverTone: Record<"complete" | "reject" | "cancel", string> = {
    complete: "hover:bg-[#ECFDF5] hover:text-[#047857]",
    reject: "hover:bg-[#FEF2F2] hover:text-[#B91C1C]",
    cancel: "hover:bg-[#F3F4F6] hover:text-[#374151]",
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
      >
        <MdMoreHoriz className="h-5 w-5" />
      </button>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          {
            name: "flip",
            options: { fallbackPlacements: ["top-start", "top-end"] },
          },
          {
            name: "preventOverflow",
            options: { padding: 8, altBoundary: true },
          },
        ]}
        style={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={6} sx={{ borderRadius: 1.25, overflow: "hidden" }}>
            <div className="w-56 divide-y divide-gray-100">
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    it.action();
                  }}
                  className={[
                    "text-slate-700 flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm",
                    "transition-colors",
                    hoverTone[it.key],
                  ].join(" ")}
                >
                  <span
                    className={[
                      "grid h-5 w-5 place-items-center rounded-full border text-[13px]",
                      it.key === "complete"
                        ? "border-[#A7F3D0] !text-[#047857]"
                        : it.key === "reject"
                          ? "border-[#FCA5A5] !text-[#B91C1C]"
                          : "border-none !text-[#B45309]",
                    ].join(" ")}
                  >
                    {it.icon}
                  </span>
                  <span className="text-gray-800">{it.label}</span>
                </button>
              ))}
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const idPill = (id: string, onCopy: (id: string) => void) => (
  <MuiTooltip title="Copy trade code" arrow>
    <button
      onClick={(e) => {
        e.stopPropagation();
        onCopy(id);
      }}
      className={[
        "py-0.3 inline-flex items-center rounded-full px-2.5",
        "font-mono text-[11px] font-semibold",
        "bg-emerald-100 text-emerald-800 ring-emerald-200 ring-1",
        "hover:bg-emerald-200/70 transition-colors",
      ].join(" ")}
    >
      {id}
      <MdOutlineContentCopy className="ml-1 h-3.5 w-3.5 opacity-60" />
    </button>
  </MuiTooltip>
);

interface TradeStatusItems {
  status: string;
  value: string;
  display: ReactElement;
}

const statusClasses = 'status';

const statusList: TradeStatusItems[] = [
  { status: "awaiting", value: 'awaiting', display: <Badge className={`${statusClasses} status-awaiting`}>Awaiting</Badge> },
  { status: "pending", value: 'pending', display: <Badge className={`${statusClasses} status-pending`}>Pending</Badge> },
  { status: "cancelled", value: 'cancelled', display: <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge> },
  { status: "completed", value: 'completed', display: <Badge className={`${statusClasses} status-completed`}>Completed</Badge> },
  { status: "rejected", value: 'rejected', display: <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge> },
];

const TradeList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadList(): void;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadList,
}) => {
    const { profileTimeZone } = useContext(ProfileContext);
    const userRole = localStorage.getItem("userRole")?.toLowerCase();

    const [swalProps, setSwalProps] = useState<any>({});

    const [tradeToCancel, setTradeToCancel] = useState<any>(null);
    const [isOpenTradeCancelConfirmModal, setIsOpenTradeCancelConfirmModal] = useState<boolean>(false);

    const [tradeToComplete, setTradeToComplete] = useState<any>(null);
    const [isOpenTradeCompleteConfirmModal, setIsOpenTradeCompleteConfirmModal] = useState<boolean>(false);

    const [tradeToReject, setTradeToReject] = useState<any>(null);
    const [isOpenTradeRejectConfirmModal, setIsOpenTradeRejectConfirmModal] = useState<boolean>(false);

    const formatDate = (ts?: string | number | null) => {
      if (!ts) return "—";

      if (!isNaN(Number(ts))) {
        const num = Number(ts);
        return dayjs(num < 1e12 ? num * 1000 : num)
          .tz(profileTimeZone)
          .format("hh:mmA D MMM YY");
      }

      return dayjs(ts).tz(profileTimeZone).format("hh:mmA D MMM YY");
    };

    const formatAddress = (address?: string): string => {
      if (!address || address.length < 8) return "N/A";
      return `${address.slice(0, 6)}…${address.slice(-4)}`;
    };

    const handleClickCopy = async (tradeId: string) => {
      try {
        await navigator.clipboard.writeText(tradeId);
      } catch (err) {
        console.error("Failed to copy trade ID: ", err);
      }
    };

    const handleClickCancel = (trade: any) => {
      setTradeToCancel(trade);
      setIsOpenTradeCancelConfirmModal(true);
    };
    const closeTradeCancelConfirmModal = () => {
      setTradeToCancel(null);
      setIsOpenTradeCancelConfirmModal(false);
    };

    const handleClickComplete = (trade: any) => {
      setTradeToComplete(trade);
      setIsOpenTradeCompleteConfirmModal(true);
    };
    const closeTradeCompleteConfirmModal = () => {
      setTradeToComplete(null);
      setIsOpenTradeCompleteConfirmModal(false);
    };

    const handleClickReject = (trade: any) => {
      setTradeToReject(trade);
      setIsOpenTradeRejectConfirmModal(true);
    };
    const closeTradeRejectConfirmModal = () => {
      setTradeToReject(null);
      setIsOpenTradeRejectConfirmModal(false);
    };

    const CustomLoadingOverlay = () => (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <CircularProgress size={40} />
      </div>
    );

    const TradeRenderer = (params: ICellRendererParams) => {
      const row = params.data;
      const status = row.status;
      const statusItem = statusList.find(item => item.status === status);

      return (
        <div className="py-3">
          <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
            <div>{row.from_asset}</div>
            <MdOutlineArrowOutward />
            <div>{row.to_asset}</div>
          </div>
          <div className="mt-1 flex items-center gap-2" style={{ height: "auto" }}>
            <span className="ml-0.5 inline-block leading-[1.6]">{idPill(row.shortid, handleClickCopy)}</span>
            <span className="inline-block leading-[1.6]">
              {userRole === 'superadmin' &&
                ((status === 'completed' && row.transaction_reference) ||
                  (status === 'rejected' && row.reason)) ? (
                <Tooltip
                  title={status === 'completed' ? "Ref. " + row.transaction_reference : "Reason: " + row.reason}
                  arrow
                  placement="top"
                >
                  <div className="cursor-help">
                    {statusItem ? statusItem.display : null}
                  </div>
                </Tooltip>
              ) : (
                statusItem ? statusItem.display : null
              )}
            </span>
          </div>
        </div>
      );
    };

    const TradeAmountRenderer = (params: ICellRendererParams) => {
      const row = params.data;
      const acceptedOffer = row.offer_history ? row.offer_history.find((item: Offer) => item.status === "accepted") : null;

      return (
        <div className="flex flex-col items-baseline py-3">
          <div className="text-base font-semibold text-gray-800">
            {row.from_network === "fiat" ? (
              <DisplayAmountWithDecimals
                amount={Number(row.from_amount)}
                currency={row.from_asset}
                decimal={row.from_asset_decimal}
              />
            ) : (
              row.from_amount
            )}
            &nbsp;{row.from_asset}
          </div>
          {acceptedOffer &&
            <div className="mt-1 text-xs text-gray-700">
              {row.to_network === "fiat" ? (
                <DisplayAmountWithDecimals
                  amount={Number(acceptedOffer.receive)}
                  currency={row.to_asset}
                  decimal={row.to_asset_decimal}
                />
              ) : (
                acceptedOffer.receive
              )}
              &nbsp;{row.to_asset}
            </div>
          }
        </div>
      );
    };

    const NetReceivedRenderer = (params: ICellRendererParams) => {
      const row = params.data;
      const acceptedOffer = row.offer_history ? row.offer_history.find((item: Offer) => item.status === "accepted") : null;
      const pair = row.rate_pair || `${row.from_asset}/${row.to_asset}`;

      if (!acceptedOffer) return null;

      return (
        <div className="leading-snug py-3">
          <div className="text-base font-semibold text-gray-800">
            {row.to_network === "fiat" ? (
              <DisplayAmountWithDecimals
                amount={Number(acceptedOffer.receive)}
                currency={row.to_asset}
                decimal={row.to_asset_decimal}
              />
            ) : (
              acceptedOffer.receive
            )}
            &nbsp;{row.to_asset}
          </div>

          <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-700">
            <MdCurrencyExchange className="h-4 w-4 opacity-70" />
            <div>
              {" "}
              {row.from_network === "fiat" ? (
                <DisplayAmountWithDecimals
                  amount={Number(acceptedOffer.rate)}
                  currency={row.from_asset}
                  decimal={row.from_asset_decimal}
                />
              ) : (
                acceptedOffer.rate
              )}
              &nbsp;{pair} <span>(Rate)</span>
            </div>
          </div>
        </div>
      );
    };

    const TraderRenderer = (params: ICellRendererParams) => {
      const row = params.data;

      return (
        <div className="leading-snug py-3">
          {row.from_network === "fiat" &&
            <>
              <div className="text-sm font-semibold text-gray-800">
                {row.to_address?.nickname || "N/A"}
              </div>
              <div className="mt-1.5 text-xs text-gray-700">
                {formatAddress(row.to_address?.address)}
                {row.to_network ? ` • ${row.to_network}` : ""}
              </div>
            </>
          }
          <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-700">
            <MdOutlinePermIdentity className="h-4 w-4 opacity-70" />
            <div> {row.subaccount?.name || row.merchant?.name || "—"}</div>
          </div>
        </div>
      );
    };

    const DatesRenderer = (params: ICellRendererParams) => {
      const row = params.data;

      return (
        <div className="flex flex-col gap-1.5 py-3">
          <MuiTooltip title="Requested" arrow placement="top">
            <div className="flex items-center gap-2 text-sm font-semibold tracking-tighter text-gray-800">
              {formatDate(row.created_at)}
            </div>
          </MuiTooltip>
          {(row.status === "completed" || row.status === "rejected") && row.processed_date &&
            <MuiTooltip title="Processed" arrow placement="top">
              <div className="flex items-center gap-2 text-xs text-gray-700">
                {" "}
                Updated at: {formatDate(row.processed_date)}
              </div>
            </MuiTooltip>
          }
        </div>
      );
    };

    const ActionsRenderer = (params: ICellRendererParams) => {
      const { status } = params.data || {};
      const canCancel = userPermissions.cancelTrade === true && status === "awaiting";
      const canComplete = userPermissions.completeTrade === true && status === "pending";
      const canReject = userPermissions.rejectTrade === true && status === "pending";

      if (!canCancel && !canComplete && !canReject) return null;

      return (
        <ActionMenuCell
          row={params.data}
          userPermissions={userPermissions}
          onCancel={(data) => handleClickCancel(data)}
          onComplete={(data) => handleClickComplete(data)}
          onReject={(data) => handleClickReject(data)}
        />
      );
    };

    const columns: ColDef[] = [
      {
        field: "status",
        headerName: "Trade",
        minWidth: 200,
        flex: 1.25,
        sortable: false,
        cellRenderer: TradeRenderer,
        autoHeight: true,
      },
      {
        field: "from_amount",
        headerName: "Trade Amount",
        minWidth: 140,
        flex: 1,
        sortable: false,
        cellRenderer: TradeAmountRenderer,
        autoHeight: true,
      },
      {
        field: "to_asset",
        headerName: "Net Received",
        minWidth: 180,
        flex: 1,
        sortable: false,
        cellRenderer: NetReceivedRenderer,
        autoHeight: true,
      },
      {
        field: "subaccount",
        headerName: "Trader",
        minWidth: 160,
        flex: 0.9,
        sortable: false,
        cellRenderer: TraderRenderer,
        autoHeight: true,
      },
      {
        field: "created_at",
        headerName: "Dates",
        minWidth: 160,
        flex: 0.9,
        sortable: false,
        cellRenderer: DatesRenderer,
        autoHeight: true,
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        width: 80,
        resizable: false,
        cellRenderer: ActionsRenderer,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end", overflow: "visible" },
      },
    ];

    const filterColumns = useCallback(() => {
      if (userRole !== "superadmin") {
        const exclude = !userPermissions.cancelTrade ? ["actions"] : [];
        return columns.filter((c) => !exclude.includes(c.field as string));
      }
      return columns;
    }, [userRole, userPermissions]);

    const defaultColDef = useMemo<ColDef>(
      () => ({
        resizable: true,
        suppressMenu: true,
      }),
      []
    );

    const hidePagination = currentLastKey === "" && page === 0;

    return (
      <>
        {isOpenTradeCancelConfirmModal && tradeToCancel && (
          <TradeCancelConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenTradeCancelConfirmModal}
            dataToProcess={tradeToCancel}
            onClose={closeTradeCancelConfirmModal}
          />
        )}
        {isOpenTradeCompleteConfirmModal && userRole === "superadmin" && tradeToComplete && (
          <TradeCompleteConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenTradeCompleteConfirmModal}
            dataToProcess={tradeToComplete}
            onClose={closeTradeCompleteConfirmModal}
          />
        )}
        {isOpenTradeRejectConfirmModal && userRole === "superadmin" && tradeToReject && (
          <TradeRejectConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenTradeRejectConfirmModal}
            dataToProcess={tradeToReject}
            onClose={closeTradeRejectConfirmModal}
          />
        )}

        <Box className="card mt-3 w-full">
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={isLoading ? [] : tableData}
              columnDefs={filterColumns()}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.id}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              loading={isLoading}
              loadingOverlayComponent={CustomLoadingOverlay}
              overlayNoRowsTemplate="No data available"
            />
          </div>

          <AgGridPagination
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPaginationChange={setPaginationModel}
            hidden={hidePagination}
          />
        </Box>

        <SweetAlert2
          {...swalProps}
          customClass={{
            actions: "custom-swal2-popup-action",
            title: "custom-swal2-popup-title",
            confirmButton:
              "custom-swal2-popup-action custom-swal2-popup-action-confirm",
            cancelButton:
              "custom-swal2-popup-action custom-swal2-popup-action-cancel",
          }}
        />
      </>
    );
  };

export default TradeList;
